import { useEffect, useState } from "react";
import { DatabaseZap, FileSpreadsheet, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const Import = () => {
    const [settings, setSettings] = useState(null);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get("/settings").then((r) => setSettings(r.data));
    }, []);

    const importDefault = async () => {
        if (!confirm("Import the 36 Vaillant boiler customers from the shipped CSV?")) return;
        setBusy(true);
        try {
            const r = await api.post("/import/csv");
            setResult(r.data);
            toast.success(`Imported ${r.data.imported} rows`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Import failed");
        } finally {
            setBusy(false);
        }
    };

    const importUploaded = async () => {
        if (!file) return toast.error("Pick a CSV file first");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/import/csv", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(r.data);
            toast.success(`Imported ${r.data.imported} rows`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Import failed");
        } finally {
            setBusy(false);
        }
    };

    const already = !!settings?.csv_imported;

    return (
        <div className="space-y-6" data-testid="import-page">
            <div>
                <p className="text-label">One-off</p>
                <h2 className="font-display font-black text-4xl uppercase tracking-tight leading-none">
                    CSV import
                </h2>
                <p className="text-neutral-600 mt-2 max-w-2xl">
                    Imports each row as one customer, one property and one completed boiler service job.
                    Every imported job is created with{" "}
                    <span className="font-mono">reminder_sent = true</span> so the daily scan won’t email
                    anyone until you flip the reminder-guard on.
                </p>
            </div>

            {already && (
                <div className="dh-card p-4" style={{ borderColor: "var(--dh-amber)" }}>
                    <p className="text-label !text-[color:var(--dh-amber)]">Heads up</p>
                    <p className="text-sm">
                        The CSV import has already been run at least once. Running it again will create
                        duplicate customers.
                    </p>
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
                <div className="dh-card p-5" data-testid="import-default-card">
                    <div className="flex items-center gap-2 mb-2">
                        <DatabaseZap size={20} />
                        <p className="text-label">Shipped list</p>
                    </div>
                    <h3 className="font-display font-black text-xl uppercase leading-tight">
                        Vaillant service candidates
                    </h3>
                    <p className="text-sm text-neutral-600 mt-2">
                        Fetches the 36-row CSV shipped with the app and imports it into MongoDB.
                    </p>
                    <button
                        className="dh-btn mt-4"
                        onClick={importDefault}
                        disabled={busy}
                        data-testid="import-default-btn"
                    >
                        <DatabaseZap size={16} /> {busy ? "Working…" : "Import shipped CSV"}
                    </button>
                </div>

                <div className="dh-card p-5" data-testid="import-upload-card">
                    <div className="flex items-center gap-2 mb-2">
                        <FileSpreadsheet size={20} />
                        <p className="text-label">Custom upload</p>
                    </div>
                    <h3 className="font-display font-black text-xl uppercase leading-tight">
                        Upload your own CSV
                    </h3>
                    <p className="text-sm text-neutral-600 mt-2">
                        Same schema:{" "}
                        <span className="font-mono">
                            name, address, install_date, model, guarantee, next_service_due, email, mobile, note
                        </span>
                        .
                    </p>
                    <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        <input
                            type="file"
                            accept=".csv,text/csv"
                            data-testid="import-file-input"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            className="dh-input"
                        />
                        <button
                            className="dh-btn"
                            onClick={importUploaded}
                            disabled={busy || !file}
                            data-testid="import-upload-btn"
                        >
                            <Upload size={16} /> {busy ? "Working…" : "Import"}
                        </button>
                    </div>
                </div>
            </div>

            {result && (
                <div className="dh-card p-5" data-testid="import-result">
                    <p className="text-label">Result</p>
                    <p className="font-display font-black text-2xl uppercase">
                        Imported {result.imported} rows
                    </p>
                    {result.skipped?.length > 0 && (
                        <div className="mt-3">
                            <p className="text-label !text-[color:var(--dh-amber)]">Skipped</p>
                            <ul className="text-sm font-mono">
                                {result.skipped.map((s, i) => (
                                    <li key={i}>
                                        {s.reason}: {JSON.stringify(s.row).slice(0, 120)}…
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Import;
